import { useState } from "react";
import api, { fmtErr } from "../lib/api";
import { PublicLayout } from "../components/PublicLayout";
import { useCaptcha, CaptchaField } from "../components/Captcha";
import { LifeBuoy, CheckCircle2, Search } from "lucide-react";

const inputCls = "mt-1 w-full bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const CATEGORIES = ["Hardware", "Software", "Network", "Access", "Email", "Other"];
const PRIORITY_OPTS = [["P4", "Low — question or minor issue"], ["P3", "Normal — something isn't working"], ["P2", "High — work is blocked"], ["P1", "Urgent — critical outage"]];
const ST_STYLE = { new: "bg-sky-50 text-sky-700 border-sky-200", in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  on_hold: "bg-amber-50 text-amber-700 border-amber-200", resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-neutral-100 text-neutral-500 border-neutral-200" };
const EMPTY = { name: "", email: "", title: "", description: "", category: "Other", priority: "P3" };

export default function Support() {
  const [tab, setTab] = useState("submit");
  const [form, setForm] = useState({ ...EMPTY });
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const cap = useCaptcha();
  const [lookup, setLookup] = useState({ number: "", email: "" });
  const [status, setStatus] = useState(null);
  const [statusErr, setStatusErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setStatusErr("");
    try {
      const { data } = await api.post("/support/tickets", { ...form, ...cap.payload, website: "" });
      setDone(data);
    } catch (err) {
      setStatusErr(fmtErr(err));
      cap.refresh();
    }
    setBusy(false);
  };

  const check = async (e) => {
    e.preventDefault();
    setStatusErr("");
    setStatus(null);
    try {
      const { data } = await api.get(`/support/tickets/status?number=${encodeURIComponent(lookup.number)}&email=${encodeURIComponent(lookup.email)}`);
      setStatus(data);
    } catch (err) { setStatusErr(fmtErr(err)); }
  };

  return (
    <PublicLayout>
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16" data-testid="support-page">
        <span className="inline-flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-800 text-xs font-semibold rounded-full px-4 py-1.5 mb-5"><LifeBuoy size={13} /> GloriPub Support</span>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight font-heading">How can we help?</h1>
        <p className="text-[#52525B] mt-3 max-w-xl leading-relaxed">Submit a ticket and our team will get back to you — or check the status of an existing request.</p>

        <div className="flex gap-2 mt-8 mb-6">
          <button data-testid="support-tab-submit" onClick={() => { setTab("submit"); setStatusErr(""); }}
            className={`px-5 py-2 rounded-full text-sm font-medium ${tab === "submit" ? "bg-neutral-900 text-white" : "bg-white border border-neutral-300 hover:bg-neutral-50"}`}>Submit a ticket</button>
          <button data-testid="support-tab-status" onClick={() => { setTab("status"); setStatusErr(""); }}
            className={`px-5 py-2 rounded-full text-sm font-medium ${tab === "status" ? "bg-neutral-900 text-white" : "bg-white border border-neutral-300 hover:bg-neutral-50"}`}>Check ticket status</button>
        </div>

        {tab === "submit" && (done ? (
          <div className="bg-white border border-emerald-200 rounded-2xl p-8 text-center" data-testid="support-success">
            <CheckCircle2 size={32} className="mx-auto text-emerald-600" />
            <h2 className="text-xl font-bold font-heading mt-3">Ticket created</h2>
            <p className="text-3xl font-mono font-bold mt-3 bg-neutral-50 border border-neutral-200 rounded-lg py-2 inline-block px-6" data-testid="support-ticket-number">{done.number}</p>
            <p className="text-sm text-neutral-500 mt-3">Save this number — use it with your email under "Check ticket status" for updates.</p>
            <button data-testid="support-new-ticket" onClick={() => { setDone(null); setForm({ ...EMPTY }); cap.refresh(); }}
              className="mt-5 px-6 py-2 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Submit another ticket</button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-white border border-neutral-200 rounded-2xl p-7" data-testid="support-form">
            {statusErr && <div data-testid="support-error" className="text-red-700 bg-red-50 border border-red-200 rounded-md p-3 text-sm mb-4">{statusErr}</div>}
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="text-sm font-medium text-neutral-700">Your name
                <input data-testid="support-name-input" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className={inputCls} /></label>
              <label className="text-sm font-medium text-neutral-700">Email
                <input data-testid="support-email-input" type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className={inputCls} /></label>
            </div>
            <label className="text-sm font-medium text-neutral-700 block mt-3">Subject
              <input data-testid="support-title-input" required minLength={3} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={inputCls} /></label>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              <label className="text-sm font-medium text-neutral-700">Category
                <select data-testid="support-category-select" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select></label>
              <label className="text-sm font-medium text-neutral-700">How urgent is it?
                <select data-testid="support-priority-select" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))} className={inputCls}>
                  {PRIORITY_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select></label>
            </div>
            <label className="text-sm font-medium text-neutral-700 block mt-3">Describe the issue
              <textarea data-testid="support-description-input" rows={5} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} className={inputCls} /></label>
            <input type="text" value="" onChange={() => {}} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
            <div className="mt-4"><CaptchaField {...cap} testId="support-captcha" /></div>
            <button data-testid="support-submit-button" disabled={busy}
              className="mt-5 px-8 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
              {busy ? "Submitting..." : "Submit ticket"}
            </button>
          </form>
        ))}

        {tab === "status" && (
          <div className="bg-white border border-neutral-200 rounded-2xl p-7" data-testid="support-status-panel">
            <form onSubmit={check} className="flex flex-wrap gap-3 items-end">
              <label className="text-sm font-medium text-neutral-700 flex-1 min-w-[150px]">Ticket number
                <input data-testid="status-number-input" required placeholder="INC00042" value={lookup.number}
                  onChange={(e) => setLookup((l) => ({ ...l, number: e.target.value }))} className={inputCls + " font-mono"} /></label>
              <label className="text-sm font-medium text-neutral-700 flex-1 min-w-[180px]">Email used
                <input data-testid="status-email-input" type="email" required value={lookup.email}
                  onChange={(e) => setLookup((l) => ({ ...l, email: e.target.value }))} className={inputCls} /></label>
              <button data-testid="status-check-button" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium"><Search size={14} /> Check</button>
            </form>
            {statusErr && <div data-testid="status-error" className="text-red-700 bg-red-50 border border-red-200 rounded-md p-3 text-sm mt-4">{statusErr}</div>}
            {status && (
              <div className="mt-5 border border-neutral-200 rounded-xl p-5" data-testid="status-result">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold">{status.number} — {status.title}</p>
                  <span data-testid="status-badge" className={`text-xs font-medium border rounded-full px-2.5 py-0.5 ${ST_STYLE[status.status] || ""}`}>{status.status.replace(/_/g, " ")}</span>
                </div>
                <p className="text-xs text-neutral-400 mt-1.5">
                  {status.priority} · {status.category} · opened {new Date(status.created_at).toLocaleString()}
                  {status.assignee_name ? ` · handled by ${status.assignee_name}` : ""}
                </p>
                {status.resolution_notes && <p className="text-sm mt-3 bg-emerald-50 border border-emerald-200 rounded-md p-3"><b>Resolution:</b> {status.resolution_notes}</p>}
                {status.recent_updates?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5">Latest updates</p>
                    {status.recent_updates.map((u, i) => (
                      <p key={i} className="text-xs text-neutral-600 border-l-2 border-neutral-200 pl-2.5 mb-1.5">
                        <b>{u.author}</b>: {u.body} <span className="text-neutral-400">— {new Date(u.at).toLocaleString()}</span>
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>
    </PublicLayout>
  );
}
